import {
  useGetBlobsQuery,
  usePackageByTypeAndPackageId,
  usePackageId,
  useProject,
  useProjectId,
  useServiceName,
} from '@consigli/hooks';
import { DocumentPackageType, type ProcessorPreferenceKey } from '@consigli/types';
import { type FC, useCallback, useMemo, useState } from 'react';

import { ServiceIdIndexValue } from '@/routes';

import { AnalysisPreviewModal } from './analysis-preview-modal';
import { isAnalysisPreviewService } from './analysis-preview-steps';

/**
 * Renders the analysis preview modal on top of the page when the user has just
 * kicked off an analysis for an Insight service. The modal shows on every visit
 * and can be dismissed until the next page load.
 *
 * The package and project are queried so the modal can gate steps that depend
 * on Advanced Control processor selection (`selectedConflictTypes`), document
 * classification (`needClassification`), and country (used by handover steps
 * such as "Functional Descriptions" and "Mandatory FDV Content").
 */
export const AnalysisPreviewOverlay: FC = () => {
  const serviceName = useServiceName();
  const projectId = useProjectId();
  const packageId = usePackageId();
  const eligible = isAnalysisPreviewService(serviceName);

  const [isOpen, setIsOpen] = useState(true);

  const { data, isLoading } = useGetBlobsQuery(
    { projectId, packageId, page: 'all' },
    { skip: !eligible || !isOpen },
  );

  const { pkg } = usePackageByTypeAndPackageId(
    projectId,
    ServiceIdIndexValue[serviceName] as DocumentPackageType,
    packageId,
  );

  const { project } = useProject(projectId);

  const { documentsCount, hasChecklist } = useMemo(() => {
    const blobs = data?.results ?? [];
    return {
      documentsCount: blobs.length,
      hasChecklist: blobs.some((blob) => blob.isChecklist),
    };
  }, [data]);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  if (!eligible || !isOpen) return null;

  return (
    <AnalysisPreviewModal
      serviceName={serviceName}
      documentsCount={documentsCount}
      hasChecklist={hasChecklist}
      needsClassification={pkg?.needClassification ?? false}
      country={project?.country ?? null}
      selectedConflictTypes={pkg?.selectedConflictTypes as ProcessorPreferenceKey[] | undefined}
      isLoading={isLoading}
      onClose={close}
    />
  );
};
